import { supabase } from "@/platform/lib/supabase";
import { scheduleReviews } from "@/tenants/einstein/features/review/api";
import type { DiagnosisQuestion, DiagnosisSession, DiagnosisSummary } from "./types";

const DIAGNOSIS_SIZE = 10;

interface RawQuestion {
  id: string;
  number: number | null;
  stem: string;
  has_base_text: boolean;
  base_text: string | null;
  alternatives: Array<{ label: string; text: string }> | null;
  correct_answer: string | null;
  discipline: string;
  topic: string | null;
  einstein_exam_papers: { year: number } | null;
}

function toDiagnosisQuestion(r: RawQuestion): DiagnosisQuestion {
  return {
    id: r.id,
    number: r.number,
    stem: r.stem,
    has_base_text: r.has_base_text,
    base_text: r.base_text,
    alternatives: r.alternatives ?? [],
    correct_answer: r.correct_answer,
    discipline: r.discipline,
    topic: r.topic,
    exam_year: r.einstein_exam_papers?.year ?? 0,
  };
}

/**
 * Cria nova sessão de diagnóstico, escolhe DIAGNOSIS_SIZE questões objetivas
 * (balanceadas por disciplina quando possível) e persiste os ids em meta.
 */
export async function startDiagnosisSession(userId: string): Promise<{
  session: DiagnosisSession;
  questions: DiagnosisQuestion[];
}> {
  // Pega questões objetivas classificadas, com correct_answer válido (não ANNULLED).
  const { data: pool, error: poolErr } = await supabase
    .from("einstein_questions")
    .select(
      "id, number, stem, has_base_text, base_text, alternatives, correct_answer, discipline, topic, einstein_exam_papers!inner(year)",
    )
    .eq("kind", "objective")
    .neq("discipline", "unknown")
    .neq("correct_answer", "ANNULLED")
    .not("correct_answer", "is", null)
    .limit(200);
  if (poolErr || !pool) throw new Error(`load pool: ${poolErr?.message}`);

  const questions = pickBalanced(pool as unknown as RawQuestion[], DIAGNOSIS_SIZE);

  const { data: sessionRow, error: sessErr } = await supabase
    .from("einstein_sessions")
    .insert({
      user_id: userId,
      kind: "diagnosis",
      started_at: new Date().toISOString(),
      meta: { question_ids: questions.map((q) => q.id) },
    })
    .select("id, user_id, started_at, meta")
    .single();
  if (sessErr || !sessionRow) throw new Error(`create session: ${sessErr?.message}`);

  return {
    session: {
      id: sessionRow.id as string,
      user_id: sessionRow.user_id as string,
      started_at: sessionRow.started_at as string,
      question_ids: questions.map((q) => q.id),
    },
    questions,
  };
}

/**
 * Carrega sessão existente + questões (reload-safe).
 */
export async function loadDiagnosisSession(sessionId: string): Promise<{
  session: DiagnosisSession;
  questions: DiagnosisQuestion[];
}> {
  const { data: sess, error: sessErr } = await supabase
    .from("einstein_sessions")
    .select("id, user_id, started_at, ended_at, meta")
    .eq("id", sessionId)
    .single();
  if (sessErr || !sess) throw new Error(`session not found: ${sessErr?.message}`);

  const questionIds = (sess.meta as { question_ids?: string[] } | null)?.question_ids ?? [];
  const { data: qs, error: qErr } = await supabase
    .from("einstein_questions")
    .select(
      "id, number, stem, has_base_text, base_text, alternatives, correct_answer, discipline, topic, einstein_exam_papers!inner(year)",
    )
    .in("id", questionIds);
  if (qErr || !qs) throw new Error(`load questions: ${qErr?.message}`);

  // preservar ordem original
  const byId = new Map((qs as unknown as RawQuestion[]).map((q) => [q.id, q]));
  const questions = questionIds
    .map((id) => byId.get(id))
    .filter((q): q is RawQuestion => Boolean(q))
    .map(toDiagnosisQuestion);

  return {
    session: {
      id: sess.id as string,
      user_id: sess.user_id as string,
      started_at: sess.started_at as string,
      question_ids: questionIds,
    },
    questions,
  };
}

/**
 * Grava uma tentativa. Idempotente via upsert (mesma session_id + question_id).
 */
export async function recordAttempt(params: {
  userId: string;
  sessionId: string;
  questionId: string;
  answer: string;
  correctAnswer: string | null;
  timeSpentMs: number;
  confidence: number;
}): Promise<void> {
  const isCorrect = params.correctAnswer ? params.answer === params.correctAnswer : null;
  const { error } = await supabase.from("einstein_attempts").insert({
    user_id: params.userId,
    session_id: params.sessionId,
    question_id: params.questionId,
    answer: params.answer,
    is_correct: isCorrect,
    score: isCorrect ? 1 : 0,
    time_spent_ms: params.timeSpentMs,
    confidence: params.confidence,
  });
  if (error) throw error;
}

/**
 * Fecha a sessão (ended_at + duration_seconds + raw_score) e agenda revisões
 * espaçadas para as questões erradas/baixa-confiança da sessão.
 */
export async function endDiagnosisSession(
  sessionId: string,
  summary: { rawScore: number; durationSeconds: number; byDiscipline: Record<string, { total: number; correct: number }> },
): Promise<void> {
  const { error } = await supabase
    .from("einstein_sessions")
    .update({
      ended_at: new Date().toISOString(),
      duration_seconds: summary.durationSeconds,
      raw_score: summary.rawScore,
      meta: { by_discipline: summary.byDiscipline },
    })
    .eq("id", sessionId);
  if (error) throw error;

  // Agenda revisões SM-2 para questões com erro ou baixa confiança.
  try {
    const { data: sess } = await supabase
      .from("einstein_sessions")
      .select("user_id")
      .eq("id", sessionId)
      .single();
    if (!sess) return;
    const { data: attempts } = await supabase
      .from("einstein_attempts")
      .select("question_id, is_correct, confidence")
      .eq("session_id", sessionId);
    const candidates =
      (attempts ?? [])
        .filter(
          (a) =>
            a.is_correct === false || (typeof a.confidence === "number" && a.confidence <= 1),
        )
        .map((a) => a.question_id as string);
    if (candidates.length > 0) {
      await scheduleReviews({ userId: sess.user_id as string, questionIds: candidates });
    }
  } catch (err) {
    console.warn("[diagnosis] scheduleReviews failed", err);
  }
}

/**
 * Calcula resumo da sessão a partir das attempts gravadas.
 */
export async function computeSummary(sessionId: string): Promise<DiagnosisSummary> {
  const { data: attempts, error: aErr } = await supabase
    .from("einstein_attempts")
    .select("question_id, is_correct, score, time_spent_ms")
    .eq("session_id", sessionId);
  if (aErr || !attempts) throw new Error(`load attempts: ${aErr?.message}`);

  const { data: sess, error: sErr } = await supabase
    .from("einstein_sessions")
    .select("started_at, meta")
    .eq("id", sessionId)
    .single();
  if (sErr || !sess) throw new Error(`load session: ${sErr?.message}`);

  const questionIds = (sess.meta as { question_ids?: string[] } | null)?.question_ids ?? [];
  const { data: questions, error: qErr } = await supabase
    .from("einstein_questions")
    .select("id, discipline")
    .in("id", questionIds);
  if (qErr || !questions) throw new Error(`load questions: ${qErr?.message}`);

  const disciplineByQuestion = new Map<string, string>(
    (questions as Array<{ id: string; discipline: string }>).map((q) => [q.id, q.discipline]),
  );

  const byDisc: Record<string, { total: number; correct: number }> = {};
  let correct = 0;
  let totalTimeMs = 0;
  for (const a of attempts as Array<{
    question_id: string;
    is_correct: boolean | null;
    score: number;
    time_spent_ms: number | null;
  }>) {
    const d = disciplineByQuestion.get(a.question_id) ?? "unknown";
    const bucket = byDisc[d] ?? { total: 0, correct: 0 };
    bucket.total += 1;
    if (a.is_correct) {
      bucket.correct += 1;
      correct += 1;
    }
    byDisc[d] = bucket;
    totalTimeMs += a.time_spent_ms ?? 0;
  }

  const totalSec = Math.floor((Date.now() - new Date(sess.started_at as string).getTime()) / 1000);

  return {
    total: questionIds.length,
    answered: attempts.length,
    correct,
    by_discipline: byDisc,
    duration_seconds: totalSec > 0 ? totalSec : Math.floor(totalTimeMs / 1000),
    raw_score: correct,
  };
}

/**
 * Pega questões balanceadas por disciplina: 1 de cada das 8 + 2 extras aleatórios.
 */
function pickBalanced(pool: RawQuestion[], size: number): DiagnosisQuestion[] {
  const byDisc: Record<string, RawQuestion[]> = {};
  for (const q of pool) {
    const arr = byDisc[q.discipline] ?? [];
    arr.push(q);
    byDisc[q.discipline] = arr;
  }
  // shuffle each bucket
  for (const k of Object.keys(byDisc)) {
    const arr = byDisc[k];
    if (arr) {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const tmp = arr[i];
        const other = arr[j];
        if (tmp !== undefined && other !== undefined) {
          arr[i] = other;
          arr[j] = tmp;
        }
      }
    }
  }
  const picked: RawQuestion[] = [];
  // round-robin
  const disciplines = Object.keys(byDisc);
  let round = 0;
  while (picked.length < size && disciplines.length > 0) {
    let progress = false;
    for (const d of disciplines) {
      const bucket = byDisc[d];
      if (bucket && bucket[round]) {
        picked.push(bucket[round]!);
        progress = true;
        if (picked.length >= size) break;
      }
    }
    if (!progress) break;
    round += 1;
  }
  return picked.slice(0, size).map(toDiagnosisQuestion);
}
